/** Parent module from loadEffectivePermissions (S&M rows share moduleId/moduleName). */
export const SM_SETUP_AND_MAINTENANCE_PARENT_MODULE_NAMES = [
  'Setup and Maintenance',
  'Setup & Maintenance',
] as const;

/** sm-dashboard child route → R&P pageId (from loadEffectivePermissions). */
export const SM_ROUTE_PAGE_ID: Readonly<Record<string, number>> = {
  'account': 400,
  'agent': 401,
  'agent-types': 402,
  'airports': 403,
  'airlines': 404,
  'ap-charge-codes': 405,
  'clearance-types': 406,
  'currency-management': 407,
  'delivery-zone': 408,
  'facilities': 409,
  'fullman-upload': 410,
  'hs-codes': 411,
  'ibc-charge-codes': 412,
  'number-ranges': 413,
  'rate-scales': 414,
  'roles-and-permissions': 415,
  'routing': 416,
  'service-types': 417,
  'service-type-groups': 418,
  'status-codes': 419,
  'user-management': 420,
};

export type SmRouteKey = keyof typeof SM_ROUTE_PAGE_ID;

export const SM_ROUTE_PAGE_NAME: Readonly<Record<SmRouteKey, string>> = {
  account: 'Accounts',
  agent: 'Agents',
  'agent-types': 'Agent Types',
  airports: 'Airports',
  airlines: 'Airlines',
  'ap-charge-codes': 'AP Charge Codes',
  'clearance-types': 'Clearance Types',
  'currency-management': 'Currency Management',
  'delivery-zone': 'Delivery Zone',
  facilities: 'Facilities',
  'fullman-upload': 'Fullman File Upload',
  'hs-codes': 'HS Codes',
  'ibc-charge-codes': 'IBC Charge Codes',
  'number-ranges': 'Number Ranges',
  'rate-scales': 'Rate Scales',
  'roles-and-permissions': 'Roles & Permissions',
  routing: 'Routing',
  'service-types': 'Service Types',
  'service-type-groups': 'Service Type Groups',
  'status-codes': 'Status Codes',
  'user-management': 'User Management',
};

export const SM_ALL_ROUTE_KEYS = Object.keys(
  SM_ROUTE_PAGE_ID
) as SmRouteKey[];

export function getSmPageIdForRoute(route: string): number | undefined {
  return SM_ROUTE_PAGE_ID[route as SmRouteKey];
}

export function getSmPageNameForRoute(route: string): string | undefined {
  return SM_ROUTE_PAGE_NAME[route as SmRouteKey];
}

export function matchesSetupMaintenanceRoutePageName(
  routeSegment: string,
  apiPageName: string | undefined
): boolean {
  const expectedPageName = getSmPageNameForRoute(routeSegment);
  if (!expectedPageName || !apiPageName?.trim()) {
    return false;
  }
  return (
    normalizeSetupMaintenancePageName(apiPageName) ===
    normalizeSetupMaintenancePageName(expectedPageName)
  );
}

export function isSetupMaintenanceParentModuleName(moduleName: string): boolean {
  const normalized = moduleName.trim().toLowerCase();
  return SM_SETUP_AND_MAINTENANCE_PARENT_MODULE_NAMES.some(
    (name) => name.trim().toLowerCase() === normalized
  );
}

/** Matches sm-dashboard card order (pageId 400–420). */
export const SETUP_MAINTENANCE_PAGE_EXPORT_ORDER: readonly string[] = [
  'Accounts',
  'Agents',
  'Agent Types',
  'Airports',
  'Airlines',
  'AP Charge Codes',
  'Clearance Types',
  'Currency Management',
  'Delivery Zone',
  'Facilities',
  'Fullman File Upload',
  'HS Codes',
  'IBC Charge Codes',
  'Number Ranges',
  'Rate Scales',
  'Roles & Permissions',
  'Routing',
  'Service Types',
  'Service Type Groups',
  'Status Codes',
  'User Management',
] as const;

export function normalizeSetupMaintenancePageName(pageName: string): string {
  return pageName
    .trim()
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/\s+/g, ' ');
}

export function getSetupMaintenancePageExportSortIndex(pageName: string): number {
  const normalized = normalizeSetupMaintenancePageName(pageName);
  const index = SETUP_MAINTENANCE_PAGE_EXPORT_ORDER.findIndex(
    name => normalizeSetupMaintenancePageName(name) === normalized
  );
  return index >= 0 ? index : SETUP_MAINTENANCE_PAGE_EXPORT_ORDER.length + 1000;
}

export function compareSetupMaintenancePageNames(
  leftPageName: string,
  rightPageName: string
): number {
  const leftIndex = getSetupMaintenancePageExportSortIndex(leftPageName);
  const rightIndex = getSetupMaintenancePageExportSortIndex(rightPageName);
  if (leftIndex !== rightIndex) {
    return leftIndex - rightIndex;
  }
  return leftPageName.localeCompare(rightPageName, undefined, {
    sensitivity: 'base',
  });
}
